import type {
  AttachmentMetadata,
  EntityId,
  JsonValue,
  UnixTimestamp,
} from "@note-app/contracts";
import { CURRENT_CONTENT_SCHEMA_VERSION } from "@note-app/contracts";
import type { IdGenerator as DomainIdGenerator } from "@note-app/domain";

import {
  KnowledgeServiceError,
  reportProgress,
  throwIfCancelled,
  type OperationContext,
} from "./errors.ts";

export interface Clock {
  now(): UnixTimestamp;
}

/**
 * Canonical callers should pass @note-app/domain's IdGenerator. The legacy
 * shape remains accepted at constructors so existing desktop adapters can
 * migrate without a flag day.
 */
export interface IdGenerator extends Partial<DomainIdGenerator> {
  /** @deprecated Implement `next()` from @note-app/domain instead. */
  nextId?(): EntityId;
}

export function nextGeneratedId(ids: IdGenerator): EntityId {
  if (ids.next) return ids.next();
  if (ids.nextId) return ids.nextId();
  throw new KnowledgeServiceError(
    "invalid-input",
    "IdGenerator must implement next().",
  );
}

export interface IncrementalHash {
  update(chunk: Uint8Array): void;
  digestHex(): string;
}

export interface HashPort {
  createSha256(): IncrementalHash;
  sha256(bytes: Uint8Array): Promise<string>;
}

export interface AttachmentFileSystem {
  createTemp(): Promise<string>;
  appendTemp(tempId: string, chunk: Uint8Array): Promise<void>;
  /**
   * Atomically makes a completed temporary file visible under storageKey.
   * Existing content is never overwritten.
   */
  commitTemp(
    tempId: string,
    storageKey: string,
  ): Promise<"created" | "already-exists">;
  discardTemp(tempId: string): Promise<void>;
  read(storageKey: string): AsyncIterable<Uint8Array>;
  stat(
    storageKey: string,
  ): Promise<{ byteLength: number; modifiedAt?: UnixTimestamp } | null>;
  delete(storageKey: string): Promise<void>;
  listBlobs?(): AsyncIterable<{
    storageKey: string;
    byteLength: number;
    modifiedAt: UnixTimestamp;
  }>;
}

/** Preferred name for new platform adapters. */
export type BlobStore = AttachmentFileSystem;

/**
 * referenceCount and unreferencedAt are rebuildable projection fields.
 * The inherited metadata and blob bytes are authoritative.
 */
export interface AttachmentRecord extends AttachmentMetadata {
  attachmentId: EntityId;
  storageKey: string;
  referenceCount: number;
  unreferencedAt?: UnixTimestamp;
}

export interface AttachmentRepository {
  get(id: EntityId): Promise<AttachmentRecord | null>;
  getBySha256(sha256: string): Promise<AttachmentRecord | null>;
  /**
   * Must be atomic for sha256 uniqueness. Losing callers receive the existing
   * record, allowing concurrent imports to deduplicate safely.
   */
  createIfAbsent(
    record: AttachmentRecord,
  ): Promise<{ record: AttachmentRecord; created: boolean }>;
  updateProjection(
    id: EntityId,
    patch: { referenceCount: number; unreferencedAt?: UnixTimestamp },
  ): Promise<AttachmentRecord>;
  list(): Promise<readonly AttachmentRecord[]>;
  /**
   * Atomically removes and returns the record only if it is still unreferenced
   * and its grace timestamp is at or before cutoff.
   */
  deleteIfUnreferenced(
    id: EntityId,
    cutoff: UnixTimestamp,
  ): Promise<AttachmentRecord | null>;
}

export interface AddAttachmentInput {
  bytes: AsyncIterable<Uint8Array>;
  originalName: string;
  mimeType: string;
  metadata?: Record<string, JsonValue>;
}

export interface AddAttachmentResult {
  attachment: AttachmentRecord;
  deduplicated: boolean;
}

export interface IntegrityResult {
  attachmentId: EntityId;
  ok: boolean;
  expectedSha256: string;
  actualSha256?: string;
  expectedByteLength: number;
  actualByteLength?: number;
  problem?: "missing" | "length-mismatch" | "checksum-mismatch" | "read-error";
}

export class AttachmentService {
  constructor(
    private readonly repository: AttachmentRepository,
    private readonly fileSystem: AttachmentFileSystem,
    private readonly hash: HashPort,
    private readonly clock: Clock,
    private readonly ids: IdGenerator,
  ) {}

  async add(
    input: AddAttachmentInput,
    context?: OperationContext,
  ): Promise<AddAttachmentResult> {
    if (input.originalName.trim() === "") {
      throw new KnowledgeServiceError(
        "invalid-input",
        "Attachment originalName must not be empty.",
      );
    }
    if (input.mimeType.trim() === "") {
      throw new KnowledgeServiceError(
        "invalid-input",
        "Attachment mimeType must not be empty.",
      );
    }

    const tempId = await this.fileSystem.createTemp();
    const hasher = this.hash.createSha256();
    let byteLength = 0;
    let committed = false;
    try {
      for await (const chunk of input.bytes) {
        throwIfCancelled(context?.signal);
        if (!(chunk instanceof Uint8Array)) {
          throw new KnowledgeServiceError(
            "invalid-input",
            "Attachment stream yielded a non-Uint8Array chunk.",
          );
        }
        hasher.update(chunk);
        byteLength += chunk.byteLength;
        await this.fileSystem.appendTemp(tempId, chunk);
        reportProgress(context, {
          phase: "attachment-write",
          completed: byteLength,
        });
      }
      throwIfCancelled(context?.signal);
      const sha256 = hasher.digestHex().toLowerCase();
      const existing = await this.repository.getBySha256(sha256);
      if (existing) {
        const existingBlob = await this.fileSystem.stat(existing.storageKey);
        if (
          existingBlob !== null &&
          existingBlob.byteLength !== existing.byteLength
        ) {
          throw new KnowledgeServiceError(
            "integrity",
            "Deduplicated attachment points to a blob with the wrong length.",
            { attachmentId: existing.id, storageKey: existing.storageKey },
          );
        }
        if (existingBlob === null) {
          await this.fileSystem.commitTemp(tempId, existing.storageKey);
          committed = true;
        } else {
          await this.fileSystem.discardTemp(tempId);
        }
        const verified = await this.verifyBlob(existing, context);
        if (!verified.ok) {
          throw new KnowledgeServiceError(
            "integrity",
            "Deduplicated attachment content failed SHA-256 verification.",
            {
              attachmentId: existing.attachmentId,
              storageKey: existing.storageKey,
              problem: verified.problem,
            },
          );
        }
        return { attachment: existing, deduplicated: true };
      }

      await this.fileSystem.commitTemp(tempId, sha256);
      committed = true;
      const now = this.clock.now();
      const attachmentId = nextGeneratedId(this.ids);
      const candidate: AttachmentRecord = {
        id: attachmentId,
        attachmentId,
        schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
        revision: 1,
        createdAt: now,
        updatedAt: now,
        sha256,
        mimeType: input.mimeType,
        byteLength,
        originalName: input.originalName,
        metadata: { ...(input.metadata ?? {}) },
        storageKey: sha256,
        referenceCount: 0,
        unreferencedAt: now,
      };
      const stored = await this.repository.createIfAbsent(candidate);
      if (!stored.created) {
        const verified = await this.verifyBlob(stored.record, context);
        if (!verified.ok) {
          throw new KnowledgeServiceError(
            "integrity",
            "Concurrent attachment deduplication resolved to invalid content.",
            { attachmentId: stored.record.attachmentId },
          );
        }
      }
      return { attachment: stored.record, deduplicated: !stored.created };
    } catch (error) {
      if (!committed) {
        try {
          await this.fileSystem.discardTemp(tempId);
        } catch (cleanupError) {
          throw new AggregateError(
            [error, cleanupError],
            "Attachment write and temporary-file cleanup both failed.",
          );
        }
      }
      throw error;
    }
  }

  async open(
    attachmentId: EntityId,
  ): Promise<AsyncIterable<Uint8Array>> {
    const record = await this.requireRecord(attachmentId);
    return this.fileSystem.read(record.storageKey);
  }

  async setReferenceCount(
    attachmentId: EntityId,
    referenceCount: number,
  ): Promise<AttachmentRecord> {
    if (!Number.isInteger(referenceCount) || referenceCount < 0) {
      throw new KnowledgeServiceError(
        "invalid-input",
        "Attachment referenceCount must be a non-negative integer.",
      );
    }
    await this.requireRecord(attachmentId);
    const patch: { referenceCount: number; unreferencedAt?: UnixTimestamp } = {
      referenceCount,
    };
    if (referenceCount === 0) {
      patch.unreferencedAt = this.clock.now();
    }
    return this.repository.updateProjection(attachmentId, patch);
  }

  async rebuildReferenceCounts(
    counts: ReadonlyMap<EntityId, number>,
    context?: OperationContext,
  ): Promise<number> {
    const records = await this.repository.list();
    let updated = 0;
    for (const record of records) {
      throwIfCancelled(context?.signal);
      await this.setReferenceCount(record.id, counts.get(record.id) ?? 0);
      updated += 1;
      reportProgress(context, {
        phase: "attachment-reference-rebuild",
        completed: updated,
        total: records.length,
      });
    }
    return updated;
  }

  async collectGarbage(
    gracePeriodMs: number,
    context?: OperationContext,
  ): Promise<readonly EntityId[]> {
    if (!Number.isFinite(gracePeriodMs) || gracePeriodMs < 0) {
      throw new KnowledgeServiceError(
        "invalid-input",
        "Attachment grace period must be non-negative.",
      );
    }
    const cutoff = this.clock.now() - gracePeriodMs;
    const records = await this.repository.list();
    const deleted: EntityId[] = [];
    for (const record of records) {
      throwIfCancelled(context?.signal);
      if (
        record.referenceCount !== 0 ||
        record.unreferencedAt === undefined ||
        record.unreferencedAt > cutoff
      ) {
        continue;
      }
      const claimed = await this.repository.deleteIfUnreferenced(
        record.id,
        cutoff,
      );
      if (!claimed) continue;
      await this.fileSystem.delete(claimed.storageKey);
      deleted.push(claimed.id);
      reportProgress(context, {
        phase: "attachment-garbage-collection",
        completed: deleted.length,
      });
    }
    return deleted;
  }

  /**
   * Removes content-addressed blobs that have no metadata row, but only after a
   * grace period. This recovers crashes between blob commit and metadata commit
   * without racing an in-flight attachment import.
   */
  async collectOrphanBlobs(
    gracePeriodMs: number,
    context?: OperationContext,
  ): Promise<readonly string[]> {
    if (!Number.isFinite(gracePeriodMs) || gracePeriodMs < 0) {
      throw new KnowledgeServiceError(
        "invalid-input",
        "Orphan blob grace period must be non-negative.",
      );
    }
    if (!this.fileSystem.listBlobs) return [];
    const referenced = new Set(
      (await this.repository.list()).map((record) => record.storageKey),
    );
    const cutoff = this.clock.now() - gracePeriodMs;
    const deleted: string[] = [];
    for await (const blob of this.fileSystem.listBlobs()) {
      throwIfCancelled(context?.signal);
      if (referenced.has(blob.storageKey) || blob.modifiedAt > cutoff) continue;
      await this.fileSystem.delete(blob.storageKey);
      deleted.push(blob.storageKey);
      reportProgress(context, {
        phase: "attachment-orphan-garbage-collection",
        completed: deleted.length,
      });
    }
    return deleted;
  }

  async checkIntegrity(
    attachmentId: EntityId,
    context?: OperationContext,
  ): Promise<IntegrityResult> {
    const record = await this.requireRecord(attachmentId);
    const stat = await this.fileSystem.stat(record.storageKey);
    if (!stat) {
      return integrityProblem(record, "missing");
    }

    const hasher = this.hash.createSha256();
    let byteLength = 0;
    try {
      for await (const chunk of this.fileSystem.read(record.storageKey)) {
        throwIfCancelled(context?.signal);
        hasher.update(chunk);
        byteLength += chunk.byteLength;
      }
    } catch {
      return integrityProblem(record, "read-error");
    }
    const actualSha256 = hasher.digestHex().toLowerCase();
    if (byteLength !== record.byteLength || stat.byteLength !== record.byteLength) {
      return integrityProblem(
        record,
        "length-mismatch",
        actualSha256,
        byteLength,
      );
    }
    if (actualSha256 !== record.sha256.toLowerCase()) {
      return integrityProblem(
        record,
        "checksum-mismatch",
        actualSha256,
        byteLength,
      );
    }
    return {
      attachmentId: record.id,
      ok: true,
      expectedSha256: record.sha256,
      actualSha256,
      expectedByteLength: record.byteLength,
      actualByteLength: byteLength,
    };
  }

  async checkAllIntegrity(
    context?: OperationContext,
  ): Promise<readonly IntegrityResult[]> {
    const records = await this.repository.list();
    const results: IntegrityResult[] = [];
    for (const record of records) {
      throwIfCancelled(context?.signal);
      results.push(await this.checkIntegrity(record.id, context));
      reportProgress(context, {
        phase: "attachment-integrity-check",
        completed: results.length,
        total: records.length,
      });
    }
    return results;
  }

  private async requireRecord(id: EntityId): Promise<AttachmentRecord> {
    const record = await this.repository.get(id);
    if (!record) {
      throw new KnowledgeServiceError(
        "not-found",
        `Attachment ${id} was not found.`,
        { attachmentId: id },
      );
    }
    return record;
  }

  private async verifyBlob(
    record: AttachmentRecord,
    context?: OperationContext,
  ): Promise<IntegrityResult> {
    const stat = await this.fileSystem.stat(record.storageKey);
    if (!stat) return integrityProblem(record, "missing");
    const hasher = this.hash.createSha256();
    let byteLength = 0;
    try {
      for await (const chunk of this.fileSystem.read(record.storageKey)) {
        throwIfCancelled(context?.signal);
        hasher.update(chunk);
        byteLength += chunk.byteLength;
      }
    } catch {
      return integrityProblem(record, "read-error");
    }
    const actualSha256 = hasher.digestHex().toLowerCase();
    if (byteLength !== record.byteLength || stat.byteLength !== record.byteLength) {
      return integrityProblem(record, "length-mismatch", actualSha256, byteLength);
    }
    if (actualSha256 !== record.sha256.toLowerCase()) {
      return integrityProblem(
        record,
        "checksum-mismatch",
        actualSha256,
        byteLength,
      );
    }
    return {
      attachmentId: record.attachmentId,
      ok: true,
      expectedSha256: record.sha256,
      actualSha256,
      expectedByteLength: record.byteLength,
      actualByteLength: byteLength,
    };
  }
}

function integrityProblem(
  record: AttachmentRecord,
  problem: NonNullable<IntegrityResult["problem"]>,
  actualSha256?: string,
  actualByteLength?: number,
): IntegrityResult {
  const result: IntegrityResult = {
    attachmentId: record.attachmentId,
    ok: false,
    expectedSha256: record.sha256,
    expectedByteLength: record.byteLength,
    problem,
  };
  if (actualSha256 !== undefined) result.actualSha256 = actualSha256;
  if (actualByteLength !== undefined) result.actualByteLength = actualByteLength;
  return result;
}

export class InMemoryAttachmentRepository implements AttachmentRepository {
  private readonly records = new Map<EntityId, AttachmentRecord>();
  private readonly idByHash = new Map<string, EntityId>();

  async get(id: EntityId): Promise<AttachmentRecord | null> {
    const record = this.records.get(id);
    return record ? cloneRecord(record) : null;
  }

  async getBySha256(sha256: string): Promise<AttachmentRecord | null> {
    const id = this.idByHash.get(sha256.toLowerCase());
    if (!id) return null;
    const record = this.records.get(id);
    return record ? cloneRecord(record) : null;
  }

  async createIfAbsent(
    record: AttachmentRecord,
  ): Promise<{ record: AttachmentRecord; created: boolean }> {
    const hash = record.sha256.toLowerCase();
    const existingId = this.idByHash.get(hash);
    if (existingId) {
      const existing = this.records.get(existingId);
      if (!existing) {
        throw new KnowledgeServiceError(
          "integrity",
          "Attachment hash index points to a missing record.",
          { sha256: hash, attachmentId: existingId },
        );
      }
      return { record: cloneRecord(existing), created: false };
    }
    const stored = cloneRecord(record);
    this.records.set(stored.id, stored);
    this.idByHash.set(hash, stored.id);
    return { record: cloneRecord(stored), created: true };
  }

  async updateProjection(
    id: EntityId,
    patch: { referenceCount: number; unreferencedAt?: UnixTimestamp },
  ): Promise<AttachmentRecord> {
    const existing = this.records.get(id);
    if (!existing) {
      throw new KnowledgeServiceError(
        "not-found",
        `Attachment ${id} was not found.`,
      );
    }
    const next: AttachmentRecord = {
      ...existing,
      referenceCount: patch.referenceCount,
    };
    if (patch.unreferencedAt !== undefined) {
      next.unreferencedAt = patch.unreferencedAt;
    } else {
      delete next.unreferencedAt;
    }
    this.records.set(id, next);
    return cloneRecord(next);
  }

  async list(): Promise<readonly AttachmentRecord[]> {
    return [...this.records.values()]
      .sort((left, right) => left.id.localeCompare(right.id))
      .map((record) => cloneRecord(record));
  }

  async deleteIfUnreferenced(
    id: EntityId,
    cutoff: UnixTimestamp,
  ): Promise<AttachmentRecord | null> {
    const existing = this.records.get(id);
    if (
      !existing ||
      existing.referenceCount !== 0 ||
      existing.unreferencedAt === undefined ||
      existing.unreferencedAt > cutoff
    ) {
      return null;
    }
    this.idByHash.delete(existing.sha256.toLowerCase());
    this.records.delete(id);
    return cloneRecord(existing);
  }
}

function cloneRecord(
  record: AttachmentRecord,
): AttachmentRecord {
  return {
    ...record,
    attachmentId: record.attachmentId || record.id,
    metadata: { ...record.metadata },
  };
}

export class InMemoryAttachmentFileSystem implements AttachmentFileSystem {
  private nextTemp = 1;
  private readonly temps = new Map<string, Uint8Array[]>();
  private readonly blobs = new Map<string, Uint8Array>();

  async createTemp(): Promise<string> {
    const id = `temp-${this.nextTemp}`;
    this.nextTemp += 1;
    this.temps.set(id, []);
    return id;
  }

  async appendTemp(tempId: string, chunk: Uint8Array): Promise<void> {
    const chunks = this.temps.get(tempId);
    if (!chunks) throw new Error(`Unknown temporary attachment ${tempId}`);
    chunks.push(chunk.slice());
  }

  async commitTemp(
    tempId: string,
    storageKey: string,
  ): Promise<"created" | "already-exists"> {
    const chunks = this.temps.get(tempId);
    if (!chunks) throw new Error(`Unknown temporary attachment ${tempId}`);
    this.temps.delete(tempId);
    if (this.blobs.has(storageKey)) return "already-exists";
    this.blobs.set(storageKey, concatenate(chunks));
    return "created";
  }

  async discardTemp(tempId: string): Promise<void> {
    this.temps.delete(tempId);
  }

  async *read(storageKey: string): AsyncIterable<Uint8Array> {
    const bytes = this.blobs.get(storageKey);
    if (!bytes) throw new Error(`Missing attachment blob ${storageKey}`);
    yield bytes.slice();
  }

  async stat(
    storageKey: string,
  ): Promise<{ byteLength: number; modifiedAt: UnixTimestamp } | null> {
    const bytes = this.blobs.get(storageKey);
    return bytes ? { byteLength: bytes.byteLength, modifiedAt: 0 } : null;
  }

  async delete(storageKey: string): Promise<void> {
    this.blobs.delete(storageKey);
  }

  async *listBlobs(): AsyncIterable<{
    storageKey: string;
    byteLength: number;
    modifiedAt: UnixTimestamp;
  }> {
    for (const [storageKey, bytes] of this.blobs) {
      yield { storageKey, byteLength: bytes.byteLength, modifiedAt: 0 };
    }
  }

  /** Test-only hook for simulating disk corruption. */
  corrupt(storageKey: string, bytes: Uint8Array): void {
    this.blobs.set(storageKey, bytes.slice());
  }

  temporaryFileCount(): number {
    return this.temps.size;
  }
}

function concatenate(chunks: readonly Uint8Array[]): Uint8Array {
  const length = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}
